import * as AvatarPrimitive from "@radix-ui/react-avatar";
import { cn } from "../lib/utils";
import { forwardRef, useMemo, type ComponentProps } from "react";
import { type ColorMode, getDeterministicHexColor, getAvatarBackground } from "@tellescope/utils";

export const Avatar = forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Root>,
  ComponentProps<typeof AvatarPrimitive.Root>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Root
    ref={ref}
    data-slot="avatar"
    className={cn("relative flex size-8 shrink-0 overflow-hidden rounded-full", className)}
    {...props}
  />
));
Avatar.displayName = "Avatar";

export const AvatarImage = forwardRef<
  React.ElementRef<typeof AvatarPrimitive.Image>,
  ComponentProps<typeof AvatarPrimitive.Image>
>(({ className, ...props }, ref) => (
  <AvatarPrimitive.Image
    ref={ref}
    data-slot="avatar-image"
    className={cn("aspect-square size-full", className)}
    {...props}
  />
));
AvatarImage.displayName = "AvatarImage";

export interface AvatarFallbackProps extends ComponentProps<typeof AvatarPrimitive.Fallback> {
  useDeterministicColor?: boolean;
  deterministicColorMode?: ColorMode;
  useStaticBackground?: boolean;
  id?: string;
}

export const AvatarFallback = forwardRef<React.ElementRef<typeof AvatarPrimitive.Fallback>, AvatarFallbackProps>(
  (
    {
      className,
      useDeterministicColor = false,
      useStaticBackground = true,
      id,
      deterministicColorMode = useDeterministicColor ? "dark" : undefined,
      ...props
    },
    ref
  ) => {
    const resolvedUseDeterministicColor = !!(useDeterministicColor && (id || props.title));
    const resolvedUseStaticBackground = !!(useStaticBackground && (id || props.title));

    const backgroundColor = useMemo(
      () => getDeterministicHexColor(id ?? props.title ?? "", { mode: deterministicColorMode }),
      [deterministicColorMode, id, props.title]
    );

    const texturedBackground = useMemo(
      () => (resolvedUseStaticBackground ? getAvatarBackground(id ?? props.title ?? "") : undefined),
      [resolvedUseStaticBackground, id, props.title]
    );

    if (resolvedUseStaticBackground && texturedBackground) {
      return (
        <AvatarPrimitive.Fallback
          ref={ref}
          data-slot="avatar-fallback"
          className={cn(
            "flex size-full items-center justify-center rounded-full text-sm font-semibold text-white",
            className
          )}
          style={{ backgroundImage: `url(${texturedBackground.svgDataUrl})`, backgroundSize: "cover" }}
          {...props}
        >
          {props.children}
        </AvatarPrimitive.Fallback>
      );
    }

    return (
      <AvatarPrimitive.Fallback
        ref={ref}
        data-slot="avatar-fallback"
        style={{
          "--background-color": backgroundColor,
          "--text-color": deterministicColorMode === "dark" ? "white" : "black",
        }}
        className={cn(
          "bg-muted flex size-full items-center justify-center rounded-full text-sm font-semibold",
          resolvedUseDeterministicColor && `bg-[var(--background-color)] text-[var(--text-color)]`,
          className
        )}
        {...props}
      />
    );
  }
);
AvatarFallback.displayName = "AvatarFallback";
